import { type InputHTMLAttributes, useId } from 'react';

type InputType = 'text' | 'email' | 'password';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'value'> {
  label?: string;
  type?: InputType;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export function Input({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  ...props
}: InputProps): JSX.Element {
  const id = useId();
  const inputId = props.id ?? id;

  const inputStyles = [
    'w-full px-3 py-2',
    'bg-surface-base',
    'border border-border-strong',
    'rounded-comp',
    'text-text-primary placeholder:text-text-secondary',
    'transition-colors duration-150',
    'focus:outline-none focus:border-accent-brand focus:ring-1 focus:ring-accent-brand/50',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    error ? 'border-accent-danger focus:border-accent-danger focus:ring-accent-danger/50' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block mb-1.5 text-sm font-medium text-text-secondary"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputStyles}
        {...props}
      />
      {error && (
        <p className="mt-1.5 text-sm text-accent-danger">{error}</p>
      )}
    </div>
  );
}
